// src/pages/PixAdminPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ExcelJS from "exceljs";
import axios from "../axiosConfig";
import "./PixAdminPage.css";

const onlyDigits = (s) => String(s || "").replace(/\D/g, "");

function formatBRL(n) {
  const v = Number(n || 0);
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDate(d) {
  if (!d) return "-";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString("pt-BR");
}

function formatDateTime(d) {
  if (!d) return "-";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleString("pt-BR");
}

function formatPercent(n) {
  const v = Number(n || 0);
  if (!Number.isFinite(v)) return "0%";
  return `${v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}


function statusBadge(st) {
  const s = String(st || "").toLowerCase();
  if (s === "pendente") return { label: "Pendente", cls: "st-pendente" };
  if (s === "aprovada") return { label: "Aprovada", cls: "st-aprovada" };
  if (s === "rejeitada") return { label: "Rejeitada", cls: "st-rejeitada" };
  if (s === "nao_elegivel") return { label: "Não elegível", cls: "st-nao-elegivel" };
  return { label: st || "—", cls: "st-outro" };
}

export default function PixAdminPage() {
  const usuario = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("usuario")) || {};
    } catch {
      return {};
    }
  }, []);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState(null);

  const [tab, setTab] = useState("todas"); // todas | pendentes | pagar | pagas | rejeitadas | nao_elegivel

  const [filters, setFilters] = useState({
    data_ini: "",
    data_fim: "",
    pago_ini: "",
    pago_fim: "",
    q: "",
    empresa: "",
  });

  // seleção para exportar contas a pagar
  const [selected, setSelected] = useState({}); // { [id]: true }
  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]).map(Number), [selected]);

  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [exportandoExcel, setExportandoExcel] = useState(false);

  const showSelection = tab === "pendentes" || tab === "pagar";

  const canSelectRow = useCallback(
    (r) => {
      const statusOperacao = String(r.status_operacao || "").toLowerCase();
      const statusPagamento = String(r.status_pagamento || "").toLowerCase();

      if (tab === "pendentes") {
        return statusOperacao === "pendente";
      }

      if (tab === "pagar") {
        return statusOperacao === "aprovada" && statusPagamento === "aguardando";
      }

      return false;
    },
    [tab]
  );

  const selectableIds = useMemo(
    () =>
      rows
        .filter((r) => canSelectRow(r))
        .map((r) => Number(r.id))
        .filter(Boolean),
    [rows, canSelectRow]
  );

  const allSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => !!selected[id]);

      const totals = useMemo(() => {
    const t = {
      qtd: 0,
      valor_operacoes: 0,
      valor_aprovadas: 0,
      pix_total: 0,
      pix_aguardando: 0,
      pix_pago: 0,
      qtd_pendentes: 0,
      qtd_aprovadas: 0,
      qtd_rejeitadas: 0,
      qtd_nao_elegivel: 0,
      selecionadas_qtd: 0,
      selecionadas_pix: 0,
    };

    const isAguardando = (r) => String(r.status_pagamento || "").toLowerCase() === "aguardando";
    const isPago = (r) => String(r.status_pagamento || "").toLowerCase() === "pago";
    const st = (r) => String(r.status_operacao || "").toLowerCase();

    for (const r of rows) {
      t.qtd += 1;
      t.valor_operacoes += Number(r.valor || 0);
      t.pix_total += Number(r.pix_valor || 0);

      const s = st(r);
      if (s === "pendente") t.qtd_pendentes += 1;
      if (s === "aprovada") t.qtd_aprovadas += 1;
      if (s === "rejeitada") t.qtd_rejeitadas += 1;
      if (s === "nao_elegivel") t.qtd_nao_elegivel += 1;

      if (s === "aprovada") {
        t.valor_aprovadas += Number(r.valor || 0);
      }


      // só faz sentido para aprovadas
      if (s === "aprovada" && isAguardando(r)) t.pix_aguardando += Number(r.pix_valor || 0);
      if (s === "aprovada" && isPago(r)) t.pix_pago += Number(r.pix_valor || 0);
    }

    // totais das selecionadas (aba pagar)
    const setSel = new Set(selectedIds);
    for (const r of rows) {
      if (!setSel.has(Number(r.id))) continue;
      t.selecionadas_qtd += 1;
      t.selecionadas_pix += Number(r.pix_valor || 0);
    }

    return t;
  }, [rows, selectedIds]);

  const pctPixPagoSobreAprovadas = useMemo(() => {
    const denom = Number(totals.valor_aprovadas || 0);
    if (!denom) return 0;
    return (Number(totals.pix_pago || 0) / denom) * 100;
  }, [totals.valor_aprovadas, totals.pix_pago]);


  // modal rejeição
  const [rejOpen, setRejOpen] = useState(false);
  const [rejId, setRejId] = useState(null);
  const [rejMotivo, setRejMotivo] = useState("");
  const [rejSubmitting, setRejSubmitting] = useState(false);

  const buildQuery = () => {
    // mapeia tab => filtros server
    let status_operacao = "";
    let status_pagamento = "";

    if (tab === "pendentes") status_operacao = "pendente";
    if (tab === "rejeitadas") status_operacao = "rejeitada";
    if (tab === "nao_elegivel") status_operacao = "nao_elegivel";

    if (tab === "pagar") {
      status_operacao = "aprovada";
      status_pagamento = "aguardando";
    }

    if (tab === "pagas") {
      status_operacao = "aprovada";
      status_pagamento = "pago";
    }

    const params = new URLSearchParams();
    if (status_operacao) params.set("status_operacao", status_operacao);
    if (status_pagamento) params.set("status_pagamento", status_pagamento);
    if (filters.data_ini) params.set("data_ini", filters.data_ini);
    if (filters.data_fim) params.set("data_fim", filters.data_fim);
    if (filters.pago_ini) params.set("pago_ini", filters.pago_ini);
    if (filters.pago_fim) params.set("pago_fim", filters.pago_fim);
    if (filters.q.trim()) params.set("q", filters.q.trim());
    if (filters.empresa.trim()) params.set("empresa", filters.empresa.trim());

    return params.toString();
  };

  const load = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const qs = buildQuery();
      const res = await axios.get(`/admin/pix/operacoes${qs ? `?${qs}` : ""}`);
      const arr = Array.isArray(res.data) ? res.data : [];
      setRows(arr);

      // ao mudar tab/filtro, limpa seleção
      setSelected({});
    } catch (e) {
      const detalhe = e?.response?.data?.error || e?.message || "Erro ao carregar Pix (admin).";
      setMsg({ type: "err", text: detalhe });
    } finally {
      setLoading(false);
    }
  }, [tab, filters.data_ini, filters.data_fim, filters.pago_ini, filters.pago_fim, filters.q, filters.empresa]);


  useEffect(() => {
    load();
    }, [load]);
 

  const applyFilters = (e) => {
    e.preventDefault();
    load();
  };

  const clearFilters = () => {
    setFilters({ data_ini: "", data_fim: "", pago_ini: "", pago_fim: "", q: "", empresa: "" });
    setTab("todas");
    setTimeout(() => load(), 0);
  };

  const toggleAll = (checked) => {
    if (!checked) {
      setSelected({});
      return;
    }

    const map = {};

    rows.forEach((r) => {
      if (canSelectRow(r)) {
        map[r.id] = true;
      }
    });

    setSelected(map);
  };

  const toggleOne = (id, checked) => {
    setSelected((p) => ({ ...p, [id]: checked }));
  };

  const openRejeitar = (id) => {
    setRejId(id);
    setRejMotivo("");
    setRejOpen(true);
  };

  const confirmarRejeicao = async () => {
    if (!rejId) return;
    if (!String(rejMotivo || "").trim()) {
      setMsg({ type: "err", text: "Informe o motivo da rejeição." });
      return;
    }

    setRejSubmitting(true);
    try {
      await axios.put(`/admin/pix/operacoes/${rejId}/rejeitar`, { motivo: String(rejMotivo).trim() });
      setRejOpen(false);
      setRejId(null);
      setRejMotivo("");
      await load();
      setMsg({ type: "ok", text: "Operação Pix rejeitada com sucesso." });
    } catch (e) {
      const detalhe = e?.response?.data?.error || e?.message || "Erro ao rejeitar operação Pix.";
      setMsg({ type: "err", text: detalhe });
    } finally {
      setRejSubmitting(false);
    }
  };

  const aprovar = async (id) => {
    try {
      await axios.put(`/admin/pix/operacoes/${id}/aprovar`);
      await load();
      setMsg({ type: "ok", text: "Operação Pix aprovada com sucesso." });
    } catch (e) {
      const detalhe = e?.response?.data?.error || e?.message || "Erro ao aprovar operação Pix.";
      setMsg({ type: "err", text: detalhe });
    }
  };

  const aprovarSelecionadas = async () => {
    if (!selectedIds.length) {
      setMsg({
        type: "err",
        text: "Selecione pelo menos uma operação pendente."
      });
      return;
    }

    const confirmar = window.confirm(
      `Deseja aprovar ${selectedIds.length} operação(ões) Pix selecionada(s)?`
    );

    if (!confirmar) return;

    setBulkSubmitting(true);

    try {
      const res = await axios.put(
        "/admin/pix/operacoes/aprovar-lote",
        { ids: selectedIds }
      );

      const processados = Number(res.data?.processados || 0);
      const ignorados = Number(res.data?.ignorados || 0);

      await load();

      setMsg({
        type: "ok",
        text:
          ignorados > 0
            ? `${processados} operação(ões) aprovada(s). ${ignorados} registro(s) ignorado(s).`
            : `${processados} operação(ões) Pix aprovada(s) com sucesso.`
      });
    } catch (e) {
      const detalhe =
        e?.response?.data?.error ||
        e?.message ||
        "Erro ao aprovar operações Pix em lote.";

      setMsg({ type: "err", text: detalhe });
    } finally {
      setBulkSubmitting(false);
    }
  };

  const marcarPago = async (id) => {
    try {
      await axios.put(`/admin/pix/operacoes/${id}/marcar-pago`);
      await load();
      setMsg({ type: "ok", text: "Pagamento marcado como PAGO." });
    } catch (e) {
      const detalhe = e?.response?.data?.error || e?.message || "Erro ao marcar pagamento.";
      setMsg({ type: "err", text: detalhe });
    }
  };

  const marcarPagosSelecionados = async () => {
      if (!selectedIds.length) {
        setMsg({
          type: "err",
          text: "Selecione pelo menos uma conta a pagar."
        });
        return;
      }

      const confirmar = window.confirm(
        `Confirma a baixa de ${selectedIds.length} pagamento(s) Pix selecionado(s)?`
      );

      if (!confirmar) return;

      setBulkSubmitting(true);

      try {
        const res = await axios.put(
          "/admin/pix/operacoes/marcar-pago-lote",
          { ids: selectedIds }
        );

        const processados = Number(res.data?.processados || 0);
        const ignorados = Number(res.data?.ignorados || 0);

        await load();

        setMsg({
          type: "ok",
          text:
            ignorados > 0
              ? `${processados} pagamento(s) marcado(s) como pago. ${ignorados} registro(s) ignorado(s).`
              : `${processados} pagamento(s) marcado(s) como pago com sucesso.`
        });
      } catch (e) {
        const detalhe =
          e?.response?.data?.error ||
          e?.message ||
          "Erro ao marcar pagamentos em lote.";

        setMsg({ type: "err", text: detalhe });
      } finally {
        setBulkSubmitting(false);
      }
    };

  const exportarSelecionadas = async () => {
    if (!selectedIds.length) {
      setMsg({ type: "err", text: "Selecione pelo menos 1 conta a pagar para exportar." });
      return;
    }

    try {
      const res = await axios.post(
        `/admin/pix/contas-a-pagar/exportar`,
        { ids: selectedIds },
        { responseType: "blob" }
      );

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pix_contas_a_pagar_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setMsg({ type: "ok", text: "Arquivo exportado com sucesso." });
    } catch (e) {
      const detalhe = e?.response?.data?.error || e?.message || "Erro ao exportar.";
      setMsg({ type: "err", text: detalhe });
    }
    };

  const exportarExcel = async () => {
    if (loading || exportandoExcel) return;

    if (!rows.length) {
      setMsg({
        type: "err",
        text: "Não existem registros para exportar."
      });
      return;
    }

    setExportandoExcel(true);
    setMsg(null);

    try {
      const nomesAbas = {
        todas: "Todas",
        pendentes: "Pendentes",
        pagar: "Contas a pagar",
        pagas: "Pagas",
        rejeitadas: "Rejeitadas",
        nao_elegivel: "Não elegíveis"
      };

      const nomeAba = nomesAbas[tab] || "Operações";

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Tikpontos";
      workbook.created = new Date();

      const worksheet = workbook.addWorksheet(
        "PIX Premiado",
        {
          views: [
            {
              state: "frozen",
              ySplit: 4
            }
          ]
        }
      );

      const colunas = [
        { header: "ID", key: "id", width: 12 },
        { header: "Pedido", key: "pedido", width: 20 },
        { header: "Cliente", key: "cliente", width: 35 },
        { header: "E-mail", key: "email", width: 38 },
        { header: "Empresa", key: "empresa", width: 38 },
        { header: "CNPJ empresa", key: "cnpj", width: 21 },
        { header: "Cadastro", key: "cadastro", width: 23 },
        { header: "Data operação", key: "data_operacao", width: 18 },
        { header: "Valor operação", key: "valor", width: 20 },
        { header: "Parcelas", key: "parcelas", width: 13 },
        { header: "Valor PIX", key: "pix", width: 20 },
        { header: "Status operação", key: "status_operacao", width: 20 },
        { header: "Status pagamento", key: "status_pagamento", width: 20 },
        { header: "Pago em", key: "data_pagamento", width: 23 }
      ];

      worksheet.columns = colunas;

      worksheet.mergeCells("A1:N1");
      worksheet.getCell("A1").value =
        `TIKPONTOS | PIX PREMIADO — ${nomeAba}`;

      worksheet.getCell("A1").font = {
        bold: true,
        size: 16,
        color: { argb: "FFFFFFFF" }
      };

      worksheet.getCell("A1").fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF1B4FD8" }
      };

      worksheet.getRow(1).height = 34;

      worksheet.mergeCells("A2:N2");
      worksheet.getCell("A2").value =
        `Relatório gerado em ${new Date().toLocaleString("pt-BR")}`;

      worksheet.getCell("A2").font = {
        italic: true,
        color: { argb: "FF64748B" }
      };

      worksheet.mergeCells("A3:N3");
      worksheet.getCell("A3").value =
        `Aba: ${nomeAba} | Registros: ${rows.length}`;

      worksheet.getCell("A3").font = {
        bold: true,
        color: { argb: "FF334155" }
      };

      const cabecalho = worksheet.getRow(4);

      colunas.forEach((coluna, indice) => {
        cabecalho.getCell(indice + 1).value =
          coluna.header;
      });

      cabecalho.height = 27;

      cabecalho.eachCell((cell) => {
        cell.font = {
          bold: true,
          color: { argb: "FFFFFFFF" }
        };

        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF334155" }
        };

        cell.alignment = {
          vertical: "middle"
        };
      });

      const converterData = (valor) => {
        if (!valor) return null;

        const data = new Date(valor);

        return Number.isNaN(data.getTime())
          ? null
          : data;
      };

      rows.forEach((r) => {
        const linha = worksheet.addRow({
          id: Number(r.id),
          pedido: String(r.numero_pedido || ""),
          cliente: r.cliente_nome || "",
          email: r.cliente_email || "",
          empresa: r.empresa_nome || "",
          cnpj: String(r.cliente_cnpj_empresa || ""),
          cadastro: converterData(r.criado_em),
          data_operacao: converterData(r.data_operacao),
          valor: Number(r.valor || 0),
          parcelas: Number(r.numero_parcelas || 0),
          pix: Number(r.pix_valor || 0),
          status_operacao: r.status_operacao || "",
          status_pagamento: r.status_pagamento || "",
          data_pagamento: converterData(r.data_pagamento)
        });

        linha.getCell(7).numFmt = "dd/mm/yyyy hh:mm";
        linha.getCell(8).numFmt = "dd/mm/yyyy";
        linha.getCell(14).numFmt = "dd/mm/yyyy hh:mm";

        linha.getCell(9).numFmt = '"R$" #,##0.00';
        linha.getCell(11).numFmt = '"R$" #,##0.00';
      });

      const ultimaLinhaDados = 4 + rows.length;
      const linhaTotal = worksheet.getRow(
        ultimaLinhaDados + 1
      );

      linhaTotal.getCell(3).value = "TOTAL";

      linhaTotal.getCell(9).value = {
        formula: `SUM(I5:I${ultimaLinhaDados})`
      };

      linhaTotal.getCell(11).value = {
        formula: `SUM(K5:K${ultimaLinhaDados})`
      };

      linhaTotal.getCell(9).numFmt =
        '"R$" #,##0.00';

      linhaTotal.getCell(11).numFmt =
        '"R$" #,##0.00';

      linhaTotal.eachCell((cell) => {
        cell.font = {
          bold: true,
          color: { argb: "FF065F46" }
        };

        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFECFDF5" }
        };
      });

      worksheet.autoFilter = {
        from: "A4",
        to: `N${ultimaLinhaDados}`
      };

      const buffer = await workbook.xlsx.writeBuffer();

      const blob = new Blob(
        [buffer],
        {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        }
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      const dataArquivo =
        new Date().toISOString().slice(0, 10);

      link.href = url;
      link.download =
        `pix_premiado_${tab}_${dataArquivo}.xlsx`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);

      setMsg({
        type: "ok",
        text:
          `${rows.length} registros exportados com sucesso. ` +
          `Valor das operações: ${formatBRL(totals.valor_operacoes)}. ` +
          `Total PIX: ${formatBRL(totals.pix_total)}.`
      });
    } catch (err) {
      console.error("Erro ao exportar PIX:", err);

      setMsg({
        type: "err",
        text: "Não foi possível gerar o arquivo Excel."
      });
    } finally {
      setExportandoExcel(false);
    }
  };

  return (
    <div className="pixa-page">
      <div className="pixa-container">
                {/* ✅ Totais (fora do header para não quebrar o layout) */}
        <div className="pixa-totals">
          <div className="pixa-total-card">
            <div className="pixa-total-label">Registros</div>
            <div className="pixa-total-value">{totals.qtd}</div>
            <div className="pixa-total-sub">
              Pendentes: <strong>{totals.qtd_pendentes}</strong> • Aprovadas: <strong>{totals.qtd_aprovadas}</strong> • Rejeitadas: <strong>{totals.qtd_rejeitadas}</strong> • Não elegíveis: <strong>{totals.qtd_nao_elegivel}</strong>
            </div>
          </div>

          <div className="pixa-total-card">
            <div className="pixa-total-label">Valor das operações</div>
            <div className="pixa-total-value">{formatBRL(totals.valor_operacoes)}</div>
            <div className="pixa-total-sub">Soma do campo “valor” (Pix Premiado)</div>
          </div>

          <div className="pixa-total-card">
            <div className="pixa-total-label">PIX total (tabela)</div>
            <div className="pixa-total-value">{formatBRL(totals.pix_total)}</div>
            <div className="pixa-total-sub">Inclui não elegíveis (R$ 0) e rejeitadas</div>
          </div>

          <div className="pixa-total-card highlight">
            <div className="pixa-total-label">A pagar (aprovadas/aguardando)</div>
            <div className="pixa-total-value">{formatBRL(totals.pix_aguardando)}</div>
            {tab === "pagar" ? (
              <div className="pixa-total-sub">
                Selecionadas: <strong>{totals.selecionadas_qtd}</strong> • Total selecionado: <strong>{formatBRL(totals.selecionadas_pix)}</strong>
              </div>
            ) : (
              <div className="pixa-total-sub">Use a aba “Contas a pagar” para selecionar e exportar</div>
            )}
          </div>

          <div className="pixa-total-card">
            <div className="pixa-total-label">Já pago</div>
            <div className="pixa-total-value">{formatBRL(totals.pix_pago)}</div>
            <div className="pixa-total-sub">Somente aprovadas com status_pagamento = pago</div>
          </div>
        </div>
        <div className="pixa-total-card">
            <div className="pixa-total-label">% PIX pago / Aprovadas</div>
            <div className="pixa-total-value">{formatPercent(pctPixPagoSobreAprovadas)}</div>
            <div className="pixa-total-sub">
              {formatBRL(totals.pix_pago)} de {formatBRL(totals.valor_aprovadas)} (valor total das aprovadas)
            </div>
          </div>

        <div className="pixa-header">
          <div>
            <h2>Pix Premiado</h2>
            <p className="pixa-sub">
             Controle completo de operações Pix: pendentes, aprovadas, contas a pagar e histórico de pagas/rejeitadas.
            </p>
          </div>

        <div className="pixa-actions">
            <button
              type="button"
              className="pixa-btn pixa-btn-export"
              onClick={exportarExcel}
              disabled={
                loading ||
                exportandoExcel ||
                !rows.length
              }
            >
              {exportandoExcel
                ? "Gerando Excel..."
                : `Exportar Excel (${rows.length})`}
            </button>

            {tab === "pendentes" && (
            <button
              className="pixa-btn success"
              onClick={aprovarSelecionadas}
              disabled={!selectedIds.length || bulkSubmitting}
            >
              {bulkSubmitting
                ? "Aprovando..."
                : `Aprovar selecionadas (${selectedIds.length})`}
            </button>
          )}

          {tab === "pagar" && (
            <>
              <button
                className="pixa-btn success"
                onClick={marcarPagosSelecionados}
                disabled={!selectedIds.length || bulkSubmitting}
              >
                {bulkSubmitting
                  ? "Processando..."
                  : `Marcar pagas (${selectedIds.length})`}
              </button>

              <button
                className="pixa-btn"
                onClick={exportarSelecionadas}
                disabled={!selectedIds.length || bulkSubmitting}
              >
                Exportar selecionadas
              </button>
            </>
          )}

          <button
            className="pixa-btn ghost"
            onClick={clearFilters}
            disabled={bulkSubmitting}
          >
            Limpar
          </button>
        </div>
        </div>

        <div className="pixa-tabs">
          {[
            ["todas", "Todas"],
            ["pendentes", "Pendentes"],
            ["pagar", "Contas a pagar"],
            ["pagas", "Pagas"],
            ["rejeitadas", "Rejeitadas"],
            ["nao_elegivel", "Não elegíveis"],
          ].map(([k, label]) => (
            <button
              key={k}
              className={`pixa-tab ${tab === k ? "on" : ""}`}
              onClick={() => setTab(k)}
            >
              {label}
            </button>
          ))}
        </div>

        <form className="pixa-filters pixa-filters-2rows" onSubmit={applyFilters}>
          <div className="pixa-field">
            <label>Data inicial (cadastro)</label>
            <input
              type="date"
              value={filters.data_ini}
              onChange={(e) => setFilters((p) => ({ ...p, data_ini: e.target.value }))}
            />
          </div>

          <div className="pixa-field">
            <label>Data final (cadastro)</label>
            <input
              type="date"
              value={filters.data_fim}
              onChange={(e) => setFilters((p) => ({ ...p, data_fim: e.target.value }))}
            />
          </div>

          

          <div className="pixa-field-pago">
            <label>Pago de</label>
            <input
              type="date"
              value={filters.pago_ini}
              onChange={(e) => setFilters((p) => ({ ...p, pago_ini: e.target.value }))}
            />
          </div>

          <div className="pixa-field-pago">
            <label>Pago até</label>
            <input
              type="date"
              value={filters.pago_fim}
              onChange={(e) => setFilters((p) => ({ ...p, pago_fim: e.target.value }))}
            />
          </div>


          <div className="pixa-field wide">
            <label>Buscar (cliente, e-mail, pedido, pagador)</label>
            <input
              value={filters.q}
              onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))}
              placeholder="Ex: Marcelo, @gmail, 221, Enayle..."
            />
          </div>

          <div className="pixa-field wide">
            <label>Empresa (razão social ou CNPJ)</label>
            <input
              value={filters.empresa}
              onChange={(e) => setFilters((p) => ({ ...p, empresa: e.target.value }))}
              placeholder="Ex: Obraimob ou 42.713..."
            />
          </div>

          <button className="pixa-btn" type="submit">
            Aplicar filtros
          </button>
        </form>

        {msg && (
          <div className={`pixa-msg ${msg.type === "ok" ? "ok" : "err"}`}>
            {msg.text}
          </div>
        )}

        <div className="pixa-table-wrap">
          <table className="pixa-table">
            <thead>
              <tr>
                {showSelection && (
                  <th style={{ width: 38 }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      disabled={!selectableIds.length || bulkSubmitting}
                      onChange={(e) => toggleAll(e.target.checked)}
                      aria-label="Selecionar tudo"
                    />
                  </th>
                )}
                <th>ID</th>
                <th>Pedido</th>
                <th>Cliente</th>
                <th>Email</th>
                <th>Empresa</th>
                <th>Cadastro</th>
                <th>Data Op.</th>
                <th>Valor</th>
                <th>Parcelas</th>
                <th>PIX</th>
                <th>Status</th>
                <th>Pagamento</th>
                <th>Pago em</th>
                <th>Ações</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={showSelection ? 15 : 14} className="pixa-empty">
                    Carregando...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={showSelection ? 15 : 14} className="pixa-empty">
                    Nenhum registro encontrado.
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const st = statusBadge(r.status_operacao);
                  const pago = String(r.status_pagamento || "").toLowerCase() === "pago";
                  const aguardando = String(r.status_pagamento || "").toLowerCase() === "aguardando";
                  const isAprovada = String(r.status_operacao || "").toLowerCase() === "aprovada";
                  const isPendente = String(r.status_operacao || "").toLowerCase() === "pendente";
                  const isRejeitada = String(r.status_operacao || "").toLowerCase() === "rejeitada";
                  const canSelect = canSelectRow(r);

                  return (
                    <React.Fragment key={r.id}>
                      <tr>
                        {showSelection && (
                          <td>
                            <input
                              type="checkbox"
                              checked={!!selected[r.id]}
                              disabled={!canSelect || bulkSubmitting}
                              onChange={(e) => toggleOne(r.id, e.target.checked)}
                            />
                          </td>
                        )}

                        <td>{r.id}</td>
                        <td>{r.numero_pedido}</td>
                        <td>{r.cliente_nome || "-"}</td>
                        <td>{r.cliente_email || "-"}</td>
                        <td className="pixa-empresa">
                          <div className="pixa-emp-nome">{r.empresa_nome || "-"}</div>
                          <div className="pixa-emp-cnpj">{r.cliente_cnpj_empresa || ""}</div>
                        </td>
                        <td>{formatDateTime(r.criado_em)}</td>
                        <td>{formatDate(r.data_operacao)}</td>
                        <td>{formatBRL(r.valor)}</td>
                        <td>{r.numero_parcelas}x</td>
                        <td>{formatBRL(r.pix_valor)}</td>

                        <td>
                          <span className={`pixa-badge ${st.cls}`}>{st.label}</span>
                        </td>

                        <td>
                          {pago ? (
                            <span className="pixa-paid">Pago</span>
                          ) : aguardando ? (
                            <span className="pixa-wait">Aguardando</span>
                          ) : (
                            <span className="pixa-wait">{r.status_pagamento || "-"}</span>
                          )}
                        </td>
                        <td>
                        {r.data_pagamento ? formatDateTime(r.data_pagamento) : "-"}
                      </td>

                        <td className="pixa-actions-col">
                          {isPendente && (
                            <>
                              <button className="pixa-mini ok" onClick={() => aprovar(r.id)}>
                                Aprovar
                              </button>
                              <button className="pixa-mini danger" onClick={() => openRejeitar(r.id)}>
                                Rejeitar
                              </button>
                            </>
                          )}

                          {isAprovada && aguardando && (
                            <button className="pixa-mini" onClick={() => marcarPago(r.id)}>
                              Marcar pago
                            </button>
                          )}

                          {isRejeitada && (
                            <span className="pixa-muted">—</span>
                          )}

                          {!isPendente && !isAprovada && !isRejeitada && (
                            <span className="pixa-muted">—</span>
                          )}
                        </td>
                      </tr>

                      {isRejeitada && String(r.motivo_rejeicao || "").trim() && (
                        <tr className="pixa-motivo-row">
                          <td colSpan={showSelection ? 15 : 14}>
                            <div className="pixa-motivo">
                              <strong>Motivo da rejeição:</strong> {String(r.motivo_rejeicao).trim()}
                              <span className="pixa-motivo-date">
                                • Atualizado em {formatDateTime(r.atualizado_em)}
                              </span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
                    </table>
        </div>

        <div className="pixa-table-totals">
          <div className="pixa-table-totals-title">
            <strong>Totalizador</strong>
            <span>
              Valores dos registros filtrados
            </span>
          </div>

          <div className="pixa-table-totals-items">
            <div>
              <span>Registros</span>
              <strong>{totals.qtd}</strong>
            </div>

            <div>
              <span>Valor das operações</span>
              <strong>
                {formatBRL(totals.valor_operacoes)}
              </strong>
            </div>

            <div>
              <span>Valor total PIX</span>
              <strong>
                {formatBRL(totals.pix_total)}
              </strong>
            </div>

            <div>
              <span>PIX pago</span>
              <strong>
                {formatBRL(totals.pix_pago)}
              </strong>
            </div>

            <div>
              <span>PIX a pagar</span>
              <strong>
                {formatBRL(totals.pix_aguardando)}
              </strong>
            </div>
          </div>
        </div>

        {/* Modal Rejeição */}
        {rejOpen && (
          <div className="pixa-modal-backdrop" onClick={() => !rejSubmitting && setRejOpen(false)}>
            <div className="pixa-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pixa-modal-head">
                <div>
                  <div className="pixa-modal-title">Rejeitar operação Pix</div>
                  <div className="pixa-modal-sub">
                    Escreva um motivo claro. Isso será exibido no painel do cliente.
                  </div>
                </div>
                <button
                  className="pixa-x"
                  onClick={() => !rejSubmitting && setRejOpen(false)}
                  disabled={rejSubmitting}
                >
                  ✕
                </button>
              </div>

              <div className="pixa-modal-body">
                <label className="pixa-label">Motivo da rejeição</label>
                <textarea
                  className="pixa-textarea"
                  value={rejMotivo}
                  onChange={(e) => setRejMotivo(e.target.value)}
                  placeholder="Ex: Pedido não encontrado / Divergência na chave PIX / Operação fora do mês..."
                  rows={4}
                />
              </div>

              <div className="pixa-modal-foot">
                <button className="pixa-btn ghost" onClick={() => setRejOpen(false)} disabled={rejSubmitting}>
                  Cancelar
                </button>
                <button className="pixa-btn danger" onClick={confirmarRejeicao} disabled={rejSubmitting}>
                  {rejSubmitting ? "Rejeitando..." : "Confirmar rejeição"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="pixa-footnote">
          Obs: “Contas a pagar” = operações <strong>aprovadas</strong> com pagamento <strong>aguardando</strong>.
        </div>
      </div>
    </div>
  );
}